import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { ApprovalStatusBadge } from './ApprovalStatusBadge'
import type { ApprovalRequest, PendingApproval } from '@/lib/api/types'
import { FileText, ArrowRight, Layers, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAvatarColor } from '@/lib/avatar-colors'

interface ApprovalRequestCardProps {
  approval: ApprovalRequest | PendingApproval
  onClick?: () => void
  className?: string
}

export function ApprovalRequestCard({
  approval,
  onClick,
  className,
}: ApprovalRequestCardProps) {
  const isApprovalRequest = (
    item: ApprovalRequest | PendingApproval
  ): item is ApprovalRequest => {
    return 'status' in item && 'workflowId' in item
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return formatDate(dateString)
  }

  const stageOrder = 'currentStageOrder' in approval ? approval.currentStageOrder : approval.stageOrder
  const totalStages = approval.totalStages

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-shadow cursor-pointer group',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Template Thumbnail */}
          {'templateThumbnail' in approval && approval.templateThumbnail ? (
            <img
              src={approval.templateThumbnail}
              alt=""
              className="w-16 h-16 rounded-lg object-cover shrink-0"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shrink-0">
              <FileText className="h-8 w-8 text-white" />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header Row */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {approval.templateName}
                </h3>
                {isApprovalRequest(approval) && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {approval.workflowName}
                  </p>
                )}
              </div>
              <ArrowRight className="h-5 w-5 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            {/* Status and Stage */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {isApprovalRequest(approval) && (
                <ApprovalStatusBadge status={approval.status} />
              )}
              <Badge variant="outline" className="text-xs">
                <Layers className="h-3 w-3 mr-1" />
                {'currentStageName' in approval ? approval.currentStageName : approval.stageName}
              </Badge>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Stage {stageOrder} of {totalStages}
              </span>
            </div>

            {/* Footer Row */}
            <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              {/* Submitted By */}
              <div className="flex items-center gap-1.5">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className={`text-[10px] ${getAvatarColor(approval.submittedByName).bg} ${getAvatarColor(approval.submittedByName).text}`}>
                    {getInitials(approval.submittedByName)}
                  </AvatarFallback>
                </Avatar>
                <span>{approval.submittedByName}</span>
              </div>

              {/* Submitted At */}
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{formatRelativeTime(approval.submittedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface ApprovalRequestListProps {
  approvals: (ApprovalRequest | PendingApproval)[]
  onItemClick?: (approval: ApprovalRequest | PendingApproval) => void
  emptyMessage?: string
  className?: string
}

export function ApprovalRequestList({
  approvals,
  onItemClick,
  emptyMessage = 'No approval requests found',
  className,
}: ApprovalRequestListProps) {
  if (approvals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
        <div className="p-4 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
          <FileText className="h-8 w-8 text-neutral-400" />
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {approvals.map((approval) => (
        <Link key={approval.id} to={`/approval/${approval.id}`}>
          <ApprovalRequestCard
            approval={approval}
            onClick={() => onItemClick?.(approval)}
          />
        </Link>
      ))}
    </div>
  )
}
